import React, { useCallback, createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import { setCookie, getCookie } from '../utils/cookies';

/** Roles built into the app; any other users.role value is a role added in "Użytkownicy". */
const BUILT_IN_ROLES = ['admin', 'kierownik_produkcji', 'user', 'stacja_pakowania', 'tracker'];

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface ProductionLine {
  id: number;
  name: string;
  description?: string;
}

interface ActiveItem {
  item_id: number;
  material: string | null;
  srednica: string | null;
  waga: string | null;
  kolor: string | null;
  barwnik: string | null;
  nazwa_oryg: string | null;
  assignment_id: number;
  previous_user_id: number | null;
  previous_user_name: string | null;
  started_at: string | null;
}

interface AuthContextType {
  user: User | null;
  selectedLine: ProductionLine | null;
  activeItem: ActiveItem | null;
  selectedShift: 'zmiana_1' | 'zmiana_2' | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithPin: (pin: string, lineId?: number) => Promise<ActiveItem | null>;
  loginLine: (login: string, password: string) => Promise<ProductionLine>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  setSelectedShift: (shift: 'zmiana_1' | 'zmiana_2' | null) => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isPackaging: boolean;
  /** Konto podgladowe — widzi linie, nie moze nic zmienic (backend blokuje zapisy). */
  isTracker: boolean;
  /** Rola dodana przez admina — widzi tylko zakładki ze swojego „Dostępu do zakładek”. */
  isCustomRole: boolean;
  setActiveItem: (item: ActiveItem | null) => void;
  /** Tabs the current role may open (Admin -> Dostęp do zakładek). Null until loaded. */
  tabs: string[] | null;
  /** True when the role may open the given tab key; false while tabs are still loading. */
  can: (tab: string) => boolean;
  refreshTabs: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [selectedLine, setSelectedLine] = useState<ProductionLine | null>(null);
  const [activeItem, setActiveItem] = useState<ActiveItem | null>(null);
  const [selectedShift, setSelectedShift] = useState<'zmiana_1' | 'zmiana_2' | null>(null);
  const [tabs, setTabs] = useState<string[] | null>(null);

  // Tab access is decided per role on the backend; fetched whenever the logged-in user changes.
  const refreshTabs = useCallback(async () => {
    try {
      const res = await api.get('/me/tabs');
      setTabs(res.data.tabs ?? []);
    } catch {
      setTabs([]);
    }
  }, []);

  useEffect(() => {
    if (user) refreshTabs();
    else setTabs(null);
  }, [user?.id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedLine = localStorage.getItem('selectedLine');
    const storedShift = localStorage.getItem('selectedShift') as 'zmiana_1' | 'zmiana_2' | null;
    
    // Check cookie first, then localStorage
    const cookieLine = getCookie('loggedInLine');
    const localStorageLine = localStorage.getItem('loggedInLine');
    
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
    
    // Restore logged in line if exists (line stays logged in)
    // Priority: cookie > localStorage > storedLine
    if (cookieLine) {
      try {
        const line = JSON.parse(cookieLine);
        setSelectedLine(line);
        // Sync to localStorage for compatibility
        localStorage.setItem('loggedInLine', cookieLine);
        localStorage.setItem('selectedLine', cookieLine);
      } catch (e) {
        console.error('Error parsing loggedInLine from cookie:', e);
      }
    } else if (localStorageLine) {
      const line = JSON.parse(localStorageLine);
      setSelectedLine(line);
      localStorage.setItem('selectedLine', localStorageLine);
      // Sync to cookie
      setCookie('loggedInLine', localStorageLine, 365);
    } else if (storedLine) {
      setSelectedLine(JSON.parse(storedLine));
    }
    
    // Restore selected shift
    if (storedShift && (storedShift === 'zmiana_1' || storedShift === 'zmiana_2')) {
      setSelectedShift(storedShift);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const response = await api.post('/login', { email, password });
    const { user, line, token } = response.data;
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    // Layout compares this with the last shift change to log out the previous shift.
    localStorage.setItem('loggedInAt', String(Date.now()));
    if (line) {
      localStorage.setItem('selectedLine', JSON.stringify(line));
      setSelectedLine(line);
    } else {
      localStorage.removeItem('selectedLine');
      setSelectedLine(null);
    }
    setUser(user);
  };

  const loginLine = async (login: string, password: string): Promise<ProductionLine> => {
    const response = await api.post('/login-line', { login, password });
    const { line } = response.data;
    
    // Store logged in line (stays logged in until admin logs it out)
    // Save to both localStorage and cookie
    const lineJson = JSON.stringify(line);
    localStorage.setItem('loggedInLine', lineJson);
    localStorage.setItem('selectedLine', lineJson);
    
    // Save to cookie - encode value to handle special characters
    try {
      setCookie('loggedInLine', lineJson, 365); // Permanent cookie (365 days)
    } catch (e) {
      console.error('Error saving line to cookie:', e);
    }
    
    setSelectedLine(line);
    return line;
  };

  const loginWithPin = async (pin: string, lineId?: number) => {
    const requestData: any = { pin };
    if (lineId) {
      requestData.line_id = lineId;
    }
    
    const response = await api.post('/login', requestData);
    const { user, line, token, active_item } = response.data;
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    // Layout compares this with the last shift change to log out the previous shift.
    localStorage.setItem('loggedInAt', String(Date.now()));
    if (line) {
      localStorage.setItem('selectedLine', JSON.stringify(line));
      setSelectedLine(line);
    } else {
      localStorage.removeItem('selectedLine');
      setSelectedLine(null);
    }
    setUser(user);
    
    // Set active item if it exists - do this synchronously
    // Check if active_item exists and has content
    const hasActiveItem = active_item && 
                         typeof active_item === 'object' && 
                         active_item !== null && 
                         Object.keys(active_item).length > 0;
    
    if (hasActiveItem) {
      setActiveItem(active_item);
      // Return active_item so it can be used directly in handlePinSubmit
      return active_item;
    } else {
      setActiveItem(null);
      return null;
    }
  };

  const register = async (name: string, email: string, password: string) => {
    const response = await api.post('/register', { name, email, password, password_confirmation: password });
    const { user, token } = response.data;
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    // Layout compares this with the last shift change to log out the previous shift.
    localStorage.setItem('loggedInAt', String(Date.now()));
    setUser(user);
  };


  const setSelectedShiftHandler = (shift: 'zmiana_1' | 'zmiana_2' | null) => {
    setSelectedShift(shift);
    if (shift) {
      localStorage.setItem('selectedShift', shift);
    } else {
      localStorage.removeItem('selectedShift');
    }
  };

  const logout = () => {
    // Remove only user authentication data
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('loggedInAt');
    setUser(null);
    setActiveItem(null);
    // Reset shift selection on logout
    setSelectedShift(null);
    localStorage.removeItem('selectedShift');
    
    // KEEP production line in context, localStorage and cookie - DO NOT REMOVE IT
    // Line stays logged in even after user logout
    // This allows any user to login to the same line using their PIN
    // Ensure line is still in localStorage and cookie
    if (selectedLine) {
      const lineJson = JSON.stringify(selectedLine);
      localStorage.setItem('loggedInLine', lineJson);
      localStorage.setItem('selectedLine', lineJson);
      setCookie('loggedInLine', lineJson, 365);
    }
    
    api.post('/logout').catch(() => {});
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        selectedLine,
        activeItem,
        selectedShift,
        login,
        loginWithPin,
        loginLine,
        register,
        logout,
        setSelectedShift: setSelectedShiftHandler,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isManager: user?.role === 'admin' || user?.role === 'kierownik_produkcji',
        isPackaging: user?.role === 'stacja_pakowania',
        isTracker: user?.role === 'tracker',
        isCustomRole: !!user && !BUILT_IN_ROLES.includes(user.role),
        setActiveItem,
        tabs,
        can: (tab: string) => !!tabs && tabs.includes(tab),
        refreshTabs,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

